import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import { faSubtract } from "@fortawesome/free-solid-svg-icons";
import { faMultiply } from "@fortawesome/free-solid-svg-icons";
import { faDivide } from "@fortawesome/free-solid-svg-icons";
import { faEquals } from "@fortawesome/free-solid-svg-icons";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { width } from "@fortawesome/free-brands-svg-icons/fa11ty";

const Calculator = () => {
  const [screen, setScreen] = useState("0");
  const [operation, setOperation] = useState("");

  const handlePress = (e, pressed) => {
    if (pressed) {
      e.target.style.transform = "translateY(2px)";
      e.target.style.boxShadow = `
      inset -4px 0 4px rgba(0, 0, 0, 0.1),
      inset 0 -4px 4px rgba(0, 0, 0, 0.15),
      0 0 0 2px rgba(0, 0, 0, 0.5),
      5px 10px 15px rgba(0, 0, 0, 0.3)
    `;
    } else {
      e.target.style.transform = "translateY(0)";
      e.target.style.boxShadow = `
      inset -8px 0 8px rgba(0, 0, 0, 0.15),
      inset 0 -8px 8px rgba(0, 0, 0, 0.25),
      0 0 0 2px rgba(0, 0, 0, 0.75),
      10px 20px 25px rgba(0, 0, 0, 0.4)
    `;
    }
  };

  // Inject animation style globally (only once)
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
    @keyframes scanlines {
      0% { background-position: 0 0; }
      100% { background-position: 0 100px; }
    }
    .calculator-screen::after {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: repeating-linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.03) 0px,
        rgba(255, 255, 255, 0.03) 1px,
        rgba(0, 0, 0, 0.1) 2px,
        rgba(0, 0, 0, 0.1) 3px
      );
      animation: scanlines 5s linear infinite;
      pointer-events: none;
    }
  `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleButtonClick = (value) => {
    if (value === "AC") {
      setScreen("0");
      setOperation("");
      return;
    }

    if (value === "=") {
      try {
        // Safely evaluate the operation using Function constructor instead of eval
        const result = new Function("return " + operation)();
        setScreen(result.toString());
        setOperation(result.toString());
      } catch {
        setScreen("Error");
      }
      return;
    }

    // Handle operator or number
    let newOperation = operation;
    if (operation === "" && ["+", "-", "*", "/"].includes(value)) {
      // prevent operator as first input
      return;
    }

    // Prevent multiple operators in a row
    if (["+", "-", "*", "/"].includes(value)) {
      const lastChar = operation.slice(-1);
      if (["+", "-", "*", "/"].includes(lastChar)) {
        newOperation = operation.slice(0, -1);
      }
    }

    newOperation += value;
    setOperation(newOperation);
    setScreen(newOperation);
  };

  const containerStyle = {
    position: "relative",
    minWidth: "300px",
    width: "100%",
    height: "100%",
    minHeight: "500px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    boxSizing: "border-box",
    border: "3px solid black",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.3)",
    padding: "10px 5px 10px 5px",
    background: "radial-gradient(circle at top left, #3a3a3aff, #1a1a1aff)", // subtle gradient background
  };

  const screenStyle = {
    position: "relative",
    width: "100%",
    height: "20%",
    background: "linear-gradient(180deg, #0088baff, #2000acff)", // deep gradient background
    color: "#ffffffff", // classic greenish LCD tint
    fontFamily: "'Digital-7', 'Courier New', monospace", // digital feel (use Courier as fallback)
    fontSize: "2.5rem",
    borderRadius: "10px 10px 0 0",
    letterSpacing: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "15px",
    boxSizing: "border-box",
    overflow: "hidden",
    border: "2px solid #001133",
    boxShadow:
      "inset 0 0 20px rgba(0, 204, 255, 0.2), 0 0 10px rgba(0, 153, 255, 0.3)", // inner glow for the screen
  };

  const keysContainerStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gridTemplateRows: "repeat(5, 1fr)",
    width: "100%",
    height: "80%",
  };

  const buttonStyle = {
    position: "relative",
    borderRadius: "10px",
    background: "linear-gradient(180deg, #282828, #202020)",
    boxShadow: `
    inset -8px 0 8px rgba(0, 0, 0, 0.15),
    inset 0 -8px 8px rgba(0, 0, 0, 0.25),
    0 0 0 2px rgba(0, 0, 0, 0.75),
    10px 20px 25px rgba(0, 0, 0, 0.4)
  `,
    color: "#e9e9e9",
    fontSize: "1.5rem",
    fontWeight: "500",
    border: "none",
    margin: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.1s ease-in-out",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <div className="calculator" style={containerStyle}>
      <div className="calculator-screen" style={screenStyle}>
        {screen}
      </div>
      <div className="calculator-keys" style={keysContainerStyle}>
        {/* Row 1 */}
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("9")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          9
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("8")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          8
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("7")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          7
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: "#f36d00ff" }}
          onClick={() => handleButtonClick("+")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faAdd} />
        </button>

        {/* Row 2 */}
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("6")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          6
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("5")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          5
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("4")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          4
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: "#f36d00ff" }}
          onClick={() => handleButtonClick("-")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faSubtract} />
        </button>

        {/* Row 3 */}
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("3")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          3
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("2")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          2
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("1")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          1
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: "#f36d00ff" }}
          onClick={() => handleButtonClick("*")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faMultiply} />
        </button>

        {/* Row 4 */}
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick(".")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          .
        </button>
        <button
          style={buttonStyle}
          onClick={() => handleButtonClick("0")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          0
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: "#c19700ff" }}
          onClick={() => handleButtonClick("=")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faEquals} />
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: "#f36d00ff" }}
          onClick={() => handleButtonClick("/")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faDivide} />
        </button>

        {/* Row 5 */}
        <button
          style={{
            ...buttonStyle,
            gridColumn: "span 4",
            backgroundColor: "#f36d00ff",
            color: "white",
          }}
          onClick={() => handleButtonClick("AC")}
          onMouseDown={(e) => handlePress(e, true)}
          onMouseUp={(e) => handlePress(e, false)}
        >
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </div>
    </div>
  );
};

export default Calculator;
